'use client';

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface KpiCardTrend {
  value: number;
  label: string;
  unit?: 'percent' | 'pp';
}

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: KpiCardTrend;
  variant?: 'default' | 'gradient';
  className?: string;
}

/**
 * Cartão KPI no mesmo formato do bloco de métricas anuais (`KpiStat`):
 * ícone + título na primeira linha, valor em destaque; tendência opcional no rodapé.
 */
const KpiCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  variant = 'default',
  className,
}: KpiCardProps) => {
  const trendSuffix = trend?.unit === 'pp' ? ' p.p.' : '%';
  const isGradient = variant === 'gradient';

  const trendTone =
    trend == null ? null : trend.value > 0 ? 'up' : trend.value < 0 ? 'down' : 'neutral';

  return (
    <div
      className={cn(
        'flex h-full min-h-[100px] w-full flex-col gap-2 rounded-xl p-3 shadow-sm sm:p-4',
        isGradient ?
          'static-gradient-bg from-navy-dark via-navy to-navy-mid border border-white/15 text-white shadow-lg shadow-navy/20'
        : 'border border-border-light bg-white',
        className
      )}
    >
      <div className="flex w-full shrink-0 justify-center px-0.5">
        <div
          className={cn(
            'flex min-w-0 max-w-full items-center justify-center gap-2 text-center',
            isGradient ? 'text-white/75' : 'text-text-muted'
          )}
        >
          <Icon
            className={cn(
              'h-[18px] w-[18px] shrink-0',
              isGradient ? 'text-cyan-light' : 'text-cyan'
            )}
            strokeWidth={2}
            aria-hidden
          />
          <p
            className={cn(
              'min-w-0 max-w-full line-clamp-2 text-[10px] font-semibold uppercase leading-tight tracking-wide sm:text-xs'
            )}
          >
            {title}
          </p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-end gap-1 text-center">
        <p
          className={cn(
            'w-full text-base font-bold tabular-nums leading-tight sm:text-lg',
            isGradient ? 'text-white' : 'text-navy-dark'
          )}
        >
          {value}
        </p>
        {subtitle ?
          <p
            className={cn(
              'w-full text-xs leading-snug',
              isGradient ? 'text-white/65' : 'text-text-muted'
            )}
          >
            {subtitle}
          </p>
        : null}
      </div>

      {trend ?
        <div className="shrink-0 border-t pt-2 sm:pt-2.5">
          <div
            className={cn(
              'flex w-full flex-wrap items-baseline justify-center gap-x-1 gap-y-0.5 text-center sm:gap-x-2 sm:gap-y-1',
              isGradient ? 'border-white/15' : ''
            )}
          >
            <span
              className={cn(
                'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums sm:rounded-md sm:px-2 sm:text-xs',
                trendTone === 'neutral' ?
                  isGradient ?
                    'bg-white/15 text-white/90'
                  : 'bg-[#f0f4f8] text-text-muted'
                : trendTone === 'up' ?
                  isGradient ?
                    'bg-white/15 text-white'
                  : 'bg-emerald-50 text-emerald-700'
                : isGradient ? 'bg-white/15 text-white'
                : 'bg-red-50 text-red-700'
              )}
            >
              {trend.value >= 0 ? '+' : ''}
              {trend.value}
              {trendSuffix}
            </span>
            <span
              className={cn(
                'max-w-full text-[10px] leading-snug sm:text-xs',
                isGradient ? 'text-white/70' : 'text-text-muted'
              )}
            >
              {trend.label}
            </span>
          </div>
        </div>
      : null}
    </div>
  );
};

export default KpiCard;
